<template>
  <div class="category-page">
    <div class="page-header-mobile d-flex align-items-center justify-content-between mb-3">
      <h2 class="mb-0 page-title-mobile">Gestión de Categorías</h2>
      <button class="btn btn-dark btn-mobile-primary" @click="openCreate">
        <i class="bi bi-plus-lg me-1"></i> Nueva Categoría
      </button>
    </div>

    <div class="mobile-context-chip d-lg-none">
      <span class="mobile-context-label">Catálogo e Inventario</span>
      <strong>Categorías</strong>
    </div>

    <div class="card d-none d-lg-block">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th style="width: 80px;" class="d-none">ID</th>
              <th>Nombre</th>
              <th>Descripción</th>
              <th style="width: 200px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="text-center py-4">Cargando...</td>
            </tr>
            <tr v-else-if="error">
              <td colspan="4" class="text-danger py-4">{{ error }}</td>
            </tr>
            <tr v-else v-for="cat in categories" :key="catKey(cat)">
              <td class="d-none">{{ catId(cat) }}</td>
              <td>
                <span class="category-row-name">
                  <img v-if="cat.logo_url" :src="cat.logo_url" alt="" class="category-logo-thumb">
                  {{ cat.nombre }}
                </span>
              </td>
              <td>{{ cat.descripcion }}</td>
              <td>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="openEdit(cat)">Editar</button>
                <button class="btn btn-outline-danger btn-sm" @click="remove(cat)">Eliminar</button>
              </td>
            </tr>
            <tr v-if="!loading && !error && categories.length === 0">
              <td colspan="4" class="text-center py-4">Sin categorías</td>
            </tr>
          </tbody>
        </table>
      </div>
      <AppPagination
        class="px-3 pb-3"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="handlePageChange"
      />
    </div>

    <div class="mobile-entity-list d-lg-none">
      <div v-if="loading" class="mobile-empty-state">
        <i class="bi bi-arrow-clockwise spin"></i>
        <span>Cargando categorías...</span>
      </div>
      <div v-else-if="error" class="mobile-empty-state error">
        <i class="bi bi-exclamation-triangle"></i>
        <span>{{ error }}</span>
      </div>
      <div v-else-if="categories.length === 0" class="mobile-empty-state">
        <i class="bi bi-bookmark"></i>
        <span>Sin categorías</span>
      </div>
      <div v-else v-for="cat in categories" :key="`mobile-${catKey(cat)}`" class="mobile-entity-card">
        <div class="mobile-entity-top">
          <div>
            <p class="mobile-entity-label">Categoría</p>
            <h3 class="mobile-entity-title">{{ cat.nombre }}</h3>
          </div>
          <span class="mobile-entity-id">#{{ catId(cat) }}</span>
        </div>
        <p class="mobile-entity-description">
          {{ cat.descripcion || 'Sin descripción' }}
        </p>
        <div class="mobile-entity-actions">
          <button class="btn btn-outline-secondary btn-sm" @click="openEdit(cat)">Editar</button>
          <button class="btn btn-outline-danger btn-sm" @click="remove(cat)">Eliminar</button>
        </div>
      </div>
      <AppPagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="handlePageChange"
      />
    </div>

    <div v-if="showModal" class="modal-backdrop fade show"></div>
    <div
      v-if="showModal"
      class="modal fade show d-block"
      id="categoria-modal"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Editar Categoría' : 'Nueva Categoría' }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent>
              <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input
                  v-model.trim="form.nombre"
                  type="text"
                  class="form-control"
                  placeholder="Nombre de la categoría"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Descripción</label>
                <textarea
                  v-model.trim="form.descripcion"
                  class="form-control"
                  rows="3"
                  placeholder="Descripción"
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Logo de categoría</label>
                <div v-if="!isEditing" class="form-text mb-2">
                  Guardá la categoría primero; el logo se sube después, editándola.
                </div>
                <template v-else>
                  <div v-if="form.logo_url" class="category-logo-preview mb-2">
                    <img :src="form.logo_url" alt="Logo de la categoría">
                  </div>
                  <input
                    type="file"
                    class="form-control"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    :disabled="logoUploading"
                    @change="handleLogoFile"
                  />
                  <small v-if="logoUploading" class="form-text">Subiendo logo...</small>
                  <small v-if="logoError" class="text-danger d-block">{{ logoError }}</small>
                </template>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeModal">Cancelar</button>
            <button class="btn btn-primary" :disabled="saving || !isFormValid" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isEditing ? 'Guardar cambios' : 'Crear' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppPagination from '@/components/AppPagination.vue';
import api from '@/services/apiService.js';

export default {
  name: 'CategoriaView',
  components: { AppPagination },
  data() {
    return {
      categories: [],
      loading: false,
      error: '',
      showModal: false,
      isEditing: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      form: {
        id: null,
        nombre: '',
        descripcion: '',
        logo_url: ''
      },
      logoUploading: false,
      logoError: ''
    };
  },
  watch: {
    showModal: {
      immediate: true,
      handler(val) {
        try {
          if (typeof document !== 'undefined' && document?.body) {
            if (val) document.body.classList.add('modal-open');
            else document.body.classList.remove('modal-open');
          }
        } catch (e) {
          console.debug('Modal toggle failed', e);
        }
      }
    }
  },
  beforeUnmount() {
    try {
      if (typeof document !== 'undefined' && document?.body) {
        document.body.classList.remove('modal-open');
      }
    } catch (e) {
      console.debug('Modal cleanup failed', e);
    }
  },
  computed: {
    isFormValid() {
      return this.form.nombre && this.form.nombre.length >= 2;
    }
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    catId(cat) {
      return cat.categoria_id ?? cat.id ?? this.catKey(cat);
    },
    catKey(cat) {
      return `${cat.categoria_id ?? cat.id ?? cat.nombre}`;
    },
    async fetchCategories() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await api.get('/api/categories', { page: this.currentPage, limit: this.pageSize });
        let list = [];
        let pagination = null;
        if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.data)) {
          list = data.data;
        } else if (Array.isArray(data?.data?.data)) {
          list = data.data.data;
          pagination = data.data.pagination ?? null;
        } else {
          list = [];
        }
        this.categories = list;
        if (pagination) {
          this.currentPage = Number(pagination.page) || this.currentPage;
          this.totalPages = Number(pagination.totalPages) || 1;
          this.pageSize = Number(pagination.limit) || this.pageSize;
        } else {
          this.totalPages = 1;
        }
      } catch (e) {
        console.error(e);
        this.error = 'No se pudieron cargar las categorías';
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.fetchCategories();
    },
    openCreate() {
      this.isEditing = false;
      this.form = { id: null, nombre: '', descripcion: '', logo_url: '' };
      this.logoError = '';
      this.showModal = true;
    },
    async openEdit(cat) {
      this.isEditing = true;
      this.logoError = '';
      const id = this.catId(cat);
      try {
        const { data } = await api.get(`/api/categories/${id}`);
        const c = data?.data ?? data;
        this.form = {
          id,
          nombre: c?.nombre ?? cat.nombre ?? '',
          descripcion: c?.descripcion ?? cat.descripcion ?? '',
          logo_url: c?.logo_url ?? cat.logo_url ?? ''
        };
      } catch (e) {
        this.form = {
          id,
          nombre: cat.nombre ?? '',
          descripcion: cat.descripcion ?? '',
          logo_url: cat.logo_url ?? ''
        };
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
      });
    },
    // Subida de logo por categoría — ver
    // SPEC/PLAN/TASKS_BACKFILL_IMAGENES_CATALOGO_v0.1.md en ecommerce-graciela/docs.
    // Solo disponible en edición: una categoría nueva no tiene id todavía.
    async handleLogoFile(event) {
      const file = event.target?.files?.[0];
      event.target.value = '';
      if (!file || !this.form.id) return;

      this.logoError = '';
      this.logoUploading = true;
      try {
        const data_url = await this.readFileAsDataUrl(file);
        const { data } = await api.post(`/api/categories/${this.form.id}/logo`, { data_url });
        const categoria = data?.data ?? data;
        this.form.logo_url = categoria?.logo_url ?? this.form.logo_url;
        await this.fetchCategories();
      } catch (e) {
        console.error(e);
        this.logoError = 'No se pudo subir el logo de la categoría';
      } finally {
        this.logoUploading = false;
      }
    },
    async save() {
      if (!this.isFormValid) return;
      this.saving = true;
      try {
        if (this.isEditing && this.form.id != null) {
          await api.put(`/api/categories/${this.form.id}`, {
            nombre: this.form.nombre,
            descripcion: this.form.descripcion
          });
        } else {
          await api.post('/api/categories', {
            nombre: this.form.nombre,
            descripcion: this.form.descripcion
          });
        }
        this.showModal = false;
        await this.fetchCategories();
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    async remove(cat) {
      const id = this.catId(cat);
      if (!id) return;
      const confirmed = await confirm({
        message: '¿Eliminar esta categoría?',
        title: 'Eliminar categoría',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar'
      });
      if (!confirmed) return;
      try {
        await api.delete(`/api/categories/${id}`);
        await this.fetchCategories();
      } catch (e) {
        console.error(e);
      }
    }
  }
};
</script>

<style scoped>
.category-page {
  max-width: 1200px;
}

.category-row-name {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.category-logo-thumb {
  width: 24px;
  height: 24px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.category-logo-preview {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.category-logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}

.card {
  border: 1px solid #e5e5e5;
  border-radius: .5rem;
}

th, td { vertical-align: middle; }

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.mobile-entity-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mobile-entity-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.mobile-entity-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 0.65rem;
}

.mobile-entity-label {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.mobile-entity-title {
  margin: 0;
  font-size: 1rem;
  color: #0f172a;
}

.mobile-entity-id {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  font-size: 0.76rem;
  font-weight: 700;
}

.mobile-entity-description {
  margin: 0;
  color: #475569;
}

.mobile-entity-actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.9rem;
}

.mobile-entity-actions .btn {
  flex: 1 1 0;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  background: #f8fafc;
  color: #64748b;
}

.mobile-empty-state.error {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fef2f2;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .btn-mobile-primary {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .mobile-entity-top,
  .mobile-entity-actions {
    flex-direction: column;
  }

  .mobile-entity-id {
    align-self: flex-start;
  }
}
</style>
